// src/components/patients/PatientDetails.jsx
import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import RadiographyPreviewModal from "../appointments/RadiographyPreviewModal";
import { getRadiographUrl } from "../../services/radiographyService";
import { downloadRadiograph, deleteRadiograph as deleteAppointmentRadiograph } from "../../services/radiographyService";
import { useAppointments } from "../../context/AppointmentsContext";
import { usePatients } from "../../context/PatientsContext";
import {
  formatNamePart,
  sanitizePhone,
  sanitizeCnp,
} from "../../utils/textUtils";

const MAX_RADIOGRAPH_FILE_BYTES = 5 * 1024 * 1024;

export default function PatientDetails({
  patient,
  onClose,
  onSave,
  currentUser = null,
  allowEditing = true,
}) {
  const { selectAppointmentsByPatient } = useAppointments();
  const { updatePatient, refreshPatients } = usePatients();

  const [previewRx, setPreviewRx] = useState(null);
  const [editing, setEditing] = useState(false);
  const [appointmentsTab, setAppointmentsTab] = useState("active");
  const [showRepresentativeModal, setShowRepresentativeModal] = useState(false);
  const [representativeSource, setRepresentativeSource] = useState(null);
  const [formData, setFormData] = useState(patient ? { ...patient } : {});
  const [rxThumbs, setRxThumbs] = useState({});
  const fileInputRef = useRef(null);

  const role = currentUser?.role || null;
  const currentEmail = currentUser?.email || "";
  const normalizedCurrentEmail = currentEmail.trim().toLowerCase();
  const assignedEmail = (patient?.medicEmail || "").trim().toLowerCase();
  const isAssignedToCurrentUser = normalizedCurrentEmail && assignedEmail === normalizedCurrentEmail;
  const isAdmin = role === "admin";
  const canEditPatient =
    allowEditing &&
    !!currentUser &&
    (isAdmin || ["doctor", "assistant"].includes(role) || isAssignedToCurrentUser);
  const canManageMedia = !!currentUser && (isAdmin || isAssignedToCurrentUser || ["doctor", "assistant"].includes(role));
  const canUploadRadiography = canManageMedia;

  const displayCity = formatNamePart((formData.city || patient?.city || "").toString());
  const representativeData =
    formData?.legalRepresentative || patient?.legalRepresentative || {};
  const representativeLastNameDisplay = formatNamePart(representativeData.lastName || "");
  const representativeFirstNameDisplay = formatNamePart(representativeData.firstName || "");
  const representativeCityDisplay = formatNamePart(representativeData.city || "");
  const representativeRelationDisplay = representativeData.relation || "";
  const hasRepresentativeInfo = [
    representativeLastNameDisplay,
    representativeFirstNameDisplay,
    representativeCityDisplay,
    representativeRelationDisplay,
  ].some((value) => Boolean(value && value.trim()));

  const normalizePatientData = (data) => {
    if (!data) return {};
    const radiographs = (data.radiographs || []).map((rx) => ({
      ...rx,
      addedBy: typeof rx.addedBy === "string" ? rx.addedBy : null,
    }));
    const representative = data.legalRepresentative || {};
    const repNormalized = {
      lastName: formatNamePart(representative.lastName || ""),
      firstName: formatNamePart(representative.firstName || ""),
      city: formatNamePart(representative.city || ""),
      relation: representative.relation?.trim() || "",
    };

    return {
      ...data,
      lastName: formatNamePart(data.lastName || ""),
      firstName: formatNamePart(data.firstName || ""),
      phone: sanitizePhone(data.phone || ""),
      cnp: sanitizeCnp(data.cnp || ""),
      city: formatNamePart(data.city || ""),
      email: (data.email || "").trim(),
      notes: typeof data.notes === "string" ? data.notes.trim() : data.notes,
      isMinor: !!data.isMinor,
      legalRepresentative: repNormalized,
      radiographs,
      financialRecords: Array.isArray(data.financialRecords)
        ? data.financialRecords.map((record) => ({ ...record }))
        : [],
    };
  };

  const commitPatientData = async (changes = {}, { closeEditor = false } = {}) => {
    try {
      const merged = { ...formData, ...changes };
      const normalized = normalizePatientData(merged);
      const updated = await updatePatient(patient.id, normalized);
      const nextData = updated && Object.keys(updated).length
        ? { ...normalized, ...updated }
        : normalized;
      setFormData(nextData);
      if (onSave) {
        const silent =
          typeof changes === "object" &&
          (Object.prototype.hasOwnProperty.call(changes, "image") ||
            Object.prototype.hasOwnProperty.call(changes, "radiographs"));
        try {
          onSave(nextData, { silent });
        } catch (syncErr) {
          // Salvarea în backend a reușit; nu blocăm fluxul dacă sincronizarea locală eșuează.
          console.error("Eroare sync PatientDetails onSave:", syncErr);
        }
      }
      if (closeEditor) setEditing(false);
      return nextData;
    } catch (err) {
      toast.error("Eroare la salvare: " + err.message);
      throw err;
    }
  };

  // programările pacientului din context
  const myAppointments = useMemo(() => {
    if (!patient) return [];
    const all = selectAppointmentsByPatient(patient.id) || [];
    if (isAdmin || role === "doctor" || role === "assistant" || isAssignedToCurrentUser) {
      return all;
    }
    if (!normalizedCurrentEmail) return [];
    return all.filter((a) => {
      const creator = (a.createdBy || "").toString().trim().toLowerCase();
      return creator === normalizedCurrentEmail;
    });
  }, [patient, selectAppointmentsByPatient, normalizedCurrentEmail, isAdmin, role, isAssignedToCurrentUser]);

  useEffect(() => {
    if (!patient) {
      setFormData({});
      return;
    }
    setFormData(normalizePatientData(patient));
  }, [patient, currentEmail]);

  const visibleManualRadiographs = useMemo(() => {
    const list = formData?.radiographs || [];
    if (isAdmin || isAssignedToCurrentUser) return list;
    if (!normalizedCurrentEmail) return [];
    return list.filter((rx) => {
      const addedBy = (rx.addedBy || "").toString().trim().toLowerCase();
      return addedBy === normalizedCurrentEmail;
    });
  }, [formData?.radiographs, isAdmin, isAssignedToCurrentUser, normalizedCurrentEmail]);

  const appointmentsWithRadiographs = useMemo(
    () => myAppointments || [],
    [myAppointments]
  );

  const appointmentsByStatus = useMemo(() => {
    const list = Array.isArray(myAppointments) ? myAppointments : [];
    const byId = new Map(list.map((a) => [a.id, a]));
    const normalize = (app) => {
      const time = app.startTime || app.time || "";
      const dateStr = app.date || "";
      return { ...app, time, date: dateStr, status: app.status || "scheduled" };
    };
    const normalized = list.map(normalize);
    const active = normalized.filter((a) => a.status === "scheduled");
    const history = normalized.filter((a) => a.status && a.status !== "scheduled");
    const sortByDate = (a, b) => {
      const dateA = new Date(`${a.date || "1970-01-01"}T${a.time || "00:00"}`);
      const dateB = new Date(`${b.date || "1970-01-01"}T${b.time || "00:00"}`);
      return dateB - dateA;
    };
    return {
      byId,
      active: active.slice().sort(sortByDate),
      history: history.slice().sort(sortByDate),
    };
  }, [myAppointments]);

  const formatAppointmentDate = (appointment) => {
    if (!appointment?.date) return "-";
    const dateObj = new Date(appointment.date);
    if (Number.isNaN(dateObj.getTime())) return appointment.date;
    return dateObj.toLocaleDateString("ro-RO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatAppointmentTime = (appointment) =>
    appointment?.startTime || appointment?.time || "-";

  const getStatusLabel = (status) => {
    if (status === "cancelled") return "Anulată";
    if (status === "rescheduled") return "Reprogramată";
    if (status === "completed") return "Finalizată";
    return "Programată";
  };

  const getStatusClass = (status) => {
    if (status === "cancelled") return "bg-red-100 text-red-700 border-red-200";
    if (status === "rescheduled") return "bg-yellow-100 text-yellow-700 border-yellow-200";
    if (status === "completed") return "bg-green-100 text-green-700 border-green-200";
    return "bg-blue-100 text-blue-700 border-blue-200";
  };

  // Încarcă thumbnails pentru Rx din programările pacientului (dedupe pe mainId)
  useEffect(() => {
    if (!patient) {
      setRxThumbs({});
      return;
    }

    let cancelled = false;

    const loadThumbs = async () => {
      const thumbs = {};
      const uniqueIds = Array.from(
        new Set((appointmentsWithRadiographs || []).map((a) => a.mainAppointmentId || a.id).filter(Boolean))
      );
      const results = await Promise.allSettled(
        uniqueIds.map(async (mainId) => {
          const url = await getRadiographUrl(mainId);
          return { mainId, url };
        })
      );

      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const { mainId, url } = result.value || {};
        if (mainId && url) thumbs[mainId] = url;
      });
      if (!cancelled) setRxThumbs(thumbs);
    };

    loadThumbs();
    return () => {
      cancelled = true;
    };
  }, [patient, appointmentsWithRadiographs]);

  useEffect(() => {
    const handleRadiographChanged = async (event) => {
      const appointmentId = event?.detail?.appointmentId;
      const action = event?.detail?.action;
      if (!appointmentId) return;

      const idsForPatient = new Set(
        (appointmentsWithRadiographs || [])
          .map((a) => a.mainAppointmentId || a.id)
          .filter(Boolean)
      );
      if (!idsForPatient.has(appointmentId)) return;

      if (action === "deleted") {
        setRxThumbs((prev) => {
          const next = { ...prev };
          delete next[appointmentId];
          return next;
        });
        setPreviewRx((prev) => (prev?.appointmentId === appointmentId ? null : prev));
        if (typeof refreshPatients === "function") {
          refreshPatients().catch((error) => {
            console.error("Nu am putut sincroniza auditul după ștergere radiografie.", error);
          });
        }
        return;
      }

      if (action === "saved") {
        try {
          const url = await getRadiographUrl(appointmentId);
          if (url) {
            setRxThumbs((prev) => ({ ...prev, [appointmentId]: url }));
          }
        } catch (error) {
          console.error("Nu am putut sincroniza thumbnail-ul radiografiei.", error);
        }
      }
    };

    window.addEventListener("radiograph:changed", handleRadiographChanged);
    return () => {
      window.removeEventListener("radiograph:changed", handleRadiographChanged);
    };
  }, [appointmentsWithRadiographs, refreshPatients]);

  if (!patient) return null;

  // schimbă poza de profil
  const handleProfileImage = (e) => {
    if (!canManageMedia) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const image = typeof reader.result === "string" ? reader.result : "";
      if (!image) return;
      const previous = formData;
      setFormData((prev) => ({ ...prev, image }));
      commitPatientData({ image }).catch(() => {
        setFormData(previous);
      });
    };
    reader.readAsDataURL(file);
  };

  // adaugă radiografie manuală (salvată în patient.radiographs)
  const handleAddRadiograph = (e) => {
    if (!canUploadRadiography) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_RADIOGRAPH_FILE_BYTES) {
      toast.error("Radiografia trebuie să aibă maximum 5MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const rxId = `rx-${Date.now()}`;
      const nextRadiographs = [
        ...(formData.radiographs || []),
        {
          id: rxId,
          data: typeof reader.result === "string" ? reader.result : "",
          addedBy: currentEmail,
          fileName: file.name || "radiografie.png",
        },
      ];
      const previous = formData.radiographs || [];
      setFormData((prev) => ({ ...prev, radiographs: nextRadiographs }));
      try {
        await commitPatientData({ radiographs: nextRadiographs });
        toast.success("Radiografie salvată");
      } catch (err) {
        toast.error("Eroare la salvarea radiografiei: " + err.message);
        setFormData((prev) => ({ ...prev, radiographs: previous }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePreviewDownload = (preview) => {
    if (!preview) return;

    if (preview.source === "manual" && preview.url) {
      try {
        const link = document.createElement("a");
        link.href = preview.url;
        link.download = preview.fileName || "radiografie.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(error);
        toast.error("Nu am putut descărca radiografia.");
      }
      return;
    }

    if (preview.appointmentId) {
      downloadRadiograph(preview.appointmentId).catch((error) => {
        console.error(error);
        toast.error("Nu am putut descărca radiografia.");
      });
    }
  };

  const handleDeleteManualRadiograph = async (radiographId) => {
    if (!radiographId) return;
    const confirmed = window.confirm("Ștergi această radiografie?");
    if (!confirmed) return;
    const previous = Array.isArray(formData.radiographs) ? formData.radiographs : [];
    const nextRadiographs = previous.filter((rx) => rx.id !== radiographId);
    setFormData((prev) => ({ ...prev, radiographs: nextRadiographs }));
    try {
      await commitPatientData({ radiographs: nextRadiographs });
      toast.success("Radiografie ștearsă");
    } catch (err) {
      setFormData((prev) => ({ ...prev, radiographs: previous }));
      toast.error("Eroare la ștergerea radiografiei: " + err.message);
    }
  };

  const handleDeleteAppointmentRadiograph = async (appointmentId) => {
    if (!appointmentId) return;
    const confirmed = window.confirm("Ștergi această radiografie din programare?");
    if (!confirmed) return;
    const ok = await deleteAppointmentRadiograph(appointmentId);
    if (!ok) {
      toast.error("Eroare la ștergerea radiografiei.");
      return;
    }
    setRxThumbs((prev) => {
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
    setPreviewRx((prev) => (prev?.appointmentId === appointmentId ? null : prev));
    if (typeof refreshPatients === "function") {
      refreshPatients().catch((error) => {
        console.error("Nu am putut sincroniza datele pacientului după ștergerea radiografiei.", error);
      });
    }
    toast.success("Radiografie ștearsă");
  };

  const handleSave = () => {
    if (!canEditPatient) return;
    commitPatientData({}, { closeEditor: true });
  };

  const handleMinorToggle = (checked) => {
    if (!canEditPatient) return;
    setFormData((prev) => ({
      ...prev,
      isMinor: checked,
      legalRepresentative: ensureRepresentativeContainer(prev),
    }));
    if (checked) startMinorRepresentativeFlow();
  };

  const ensureRepresentativeContainer = (prev) =>
    prev.legalRepresentative &&
    (prev.legalRepresentative.lastName ||
      prev.legalRepresentative.firstName ||
      prev.legalRepresentative.city ||
      prev.legalRepresentative.relation)
      ? prev.legalRepresentative
      : { lastName: "", firstName: "", city: "", relation: "" };

  const openRepresentativeModal = (source = null) => {
    if (!canEditPatient) return;
    setFormData((prev) => ({
      ...prev,
      legalRepresentative: ensureRepresentativeContainer(prev),
    }));
    setRepresentativeSource(source);
    setShowRepresentativeModal(true);
  };

  const startMinorRepresentativeFlow = () => {
    if (!canEditPatient) return;
    setFormData((prev) => ({
      ...prev,
      isMinor: true,
      legalRepresentative: ensureRepresentativeContainer(prev),
    }));
    openRepresentativeModal('minor');
  };

  const openRepresentativeEditor = () => {
    if (!canEditPatient) return;
    openRepresentativeModal('manual');
  };

  const handleRepresentativeModalCancel = () => {
    const rep = formData.legalRepresentative || {};
    const hasData = Boolean((rep.lastName || "").trim() || (rep.firstName || "").trim() || (rep.city || "").trim() || (rep.relation || "").trim());
    if (!hasData && representativeSource === 'minor') {
      setFormData((prev) => ({ ...prev, isMinor: false }));
    }
    if (!hasData && representativeSource === 'manual') {
      setFormData((prev) => ({
        ...prev,
        legalRepresentative: { lastName: "", firstName: "", city: "", relation: "" },
      }));
    }
    setRepresentativeSource(null);
    setShowRepresentativeModal(false);
  };

  const handleRepresentativeModalConfirm = (data) => {
    const sanitized = {
      lastName: data.lastName?.trim() || "",
      firstName: data.firstName?.trim() || "",
      city: data.city?.trim() || "",
      relation: data.relation?.trim() || "",
    };
    const nextIsMinor = representativeSource === 'minor' ? true : formData.isMinor;
    commitPatientData({ isMinor: nextIsMinor, legalRepresentative: sanitized });
    setRepresentativeSource(null);
    setShowRepresentativeModal(false);
    toast.success("Reprezentant actualizat");
  };

  return (
    <Modal
      title={`Date pacient – ${patient.lastName} ${patient.firstName}`}
      onClose={onClose}
      size="xl"
    >
      <div className="flex flex-col lg:flex-row gap-6 max-h-[80vh] overflow-y-auto px-1">
        {/* Stânga: poza + date */}
        <div className="flex flex-col sm:flex-row lg:flex-col gap-6 flex-1">
          {/* Poza pacient */}
          <div className="relative">
            <div className="h-48 w-48 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
              {formData.image ? (
                canManageMedia ? (
                  <label className="cursor-pointer w-full h-full">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfileImage}
                    />
                    <img
                      src={formData.image}
                      alt="Poza pacient"
                      className="object-cover h-full w-full"
                    />
                  </label>
                ) : (
                  <img
                    src={formData.image}
                    alt="Poza pacient"
                    className="object-cover h-full w-full"
                  />
                )
              ) : canManageMedia ? (
                <label className="cursor-pointer flex flex-col items-center justify-center h-full w-full">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfileImage}
                  />
                  <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl">
                    +
                  </div>
                  <span className="text-gray-500 text-xs mt-2">
                    Adaugă poză
                  </span>
                </label>
              ) : (
                <div className="flex flex-col items-center justify-center h-full w-full text-gray-500 text-xs">
                  Fără fotografie
                </div>
              )}
            </div>
          </div>

          {/* Date pacient */}
          <div className="flex-1">
            {!editing ? (
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Cod pacient:</strong> {formData.code}</p>
                <p><strong>Nume:</strong> {formData.lastName}</p>
                <p><strong>Prenume:</strong> {formData.firstName}</p>
                <p><strong>CNP:</strong> {formData.cnp}</p>
                <p><strong>Telefon:</strong> {formData.phone}</p>
                <p><strong>Email:</strong> {formData.email || "-"}</p>
                <p><strong>Oraș:</strong> {displayCity || "-"}</p>
                {formData.notes && <p><strong>Obs:</strong> {formData.notes}</p>}
                <label
                  className={`inline-flex items-center gap-2 ${!canEditPatient ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={!!formData.isMinor}
                    onChange={(e) => handleMinorToggle(e.target.checked)}
                    disabled={!canEditPatient}
                  />
                  <span>Pacient minor</span>
                </label>
                {(formData.isMinor || hasRepresentativeInfo) && (
                  <div className="space-y-1 border rounded p-2 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-600 uppercase">Reprezentant legal</p>
                      {canEditPatient && (
                        <Button size="sm" variant="secondary" onClick={openRepresentativeEditor}>
                          {hasRepresentativeInfo ? "Editează" : "Adaugă"}
                        </Button>
                      )}
                    </div>
                    <p><strong>Nume:</strong> {representativeLastNameDisplay || "-"}</p>
                    <p><strong>Prenume:</strong> {representativeFirstNameDisplay || "-"}</p>
                    <p><strong>Calitate:</strong> {representativeRelationDisplay || "-"}</p>
                    <p><strong>Oraș:</strong> {representativeCityDisplay || "-"}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 text-sm text-gray-700">
                <label className="block">
                  <span>Cod pacient:</span>
                  <input
                    type="text"
                    value={formData.code || ""}
                    disabled
                    className="border rounded p-1 w-full bg-gray-100 text-gray-600"
                  />
                </label>
                <label className="block">
                  <span>Nume:</span>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="block">
                  <span>Prenume:</span>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="block">
                  <span>CNP:</span>
                  <input
                    type="text"
                    value={formData.cnp}
                    onChange={(e) => setFormData({ ...formData, cnp: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="block">
                  <span>Telefon:</span>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="block">
                  <span>Email:</span>
                  <input
                    type="email"
                    value={formData.email || ""}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="block">
                  <span>Oraș:</span>
                  <input
                    type="text"
                    value={formData.city || ""}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="block">
                  <span>Obs:</span>
                  <textarea
                    value={formData.notes || ""}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="border rounded p-1 w-full"
                  />
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!formData.isMinor}
                    onChange={(e) => handleMinorToggle(e.target.checked)}
                  />
                  <span>Pacient minor</span>
                </label>
                <div className="space-y-1 border rounded p-2 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-600 uppercase">Reprezentant legal</p>
                    <Button size="sm" variant="secondary" onClick={openRepresentativeEditor}>
                      {hasRepresentativeInfo ? "Editează" : "Adaugă"}
                    </Button>
                  </div>
                  {hasRepresentativeInfo ? (
                    <>
                      <p><strong>Nume:</strong> {representativeLastNameDisplay || "-"}</p>
                      <p><strong>Prenume:</strong> {representativeFirstNameDisplay || "-"}</p>
                      <p><strong>Calitate:</strong> {representativeRelationDisplay || "-"}</p>
                      <p><strong>Oraș:</strong> {representativeCityDisplay || "-"}</p>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">Nu există reprezentant legal setat.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dreapta: Radiografii */}
        <div className="flex-1">
          <h4 className="font-semibold mb-2">Radiografii</h4>
          {(visibleManualRadiographs.length > 0 ||
            appointmentsWithRadiographs.length > 0) ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {visibleManualRadiographs.map((rx) => (
                <div
                  key={rx.id}
                  className="relative h-20 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden"
                  onClick={() =>
                    setPreviewRx({
                      source: "manual",
                      url: rx.data,
                      manualId: rx.id,
                      fileName: rx.fileName || "radiografie.png",
                    })
                  }
                >
                  {canManageMedia ? (
                    <button
                      type="button"
                      className="absolute top-1 right-1 z-10 h-6 w-6 rounded-full bg-black/60 text-white text-xs hover:bg-black/75"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteManualRadiograph(rx.id);
                      }}
                      title="Șterge radiografia"
                      aria-label="Șterge radiografia"
                    >
                      ✕
                    </button>
                  ) : null}
                  <img src={rx.data} alt="Radiografie" className="object-cover h-full w-full" />
                </div>
              ))}

              {Array.from(
                new Map(
                  appointmentsWithRadiographs.map((a) => [
                    a.mainAppointmentId || a.id,
                    a,
                  ])
                ).values()
              ).filter((a) => {
                const mainId = a.mainAppointmentId || a.id;
                return Boolean(rxThumbs[mainId]);
              }).map((a) => {
                const mainId = a.mainAppointmentId || a.id;
                const thumb = rxThumbs[mainId];
                return (
                  <div
                    key={mainId}
                    className="relative h-20 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden"
                    onClick={() =>
                      thumb &&
                      setPreviewRx({
                        source: "appointment",
                        url: thumb,
                        appointmentId: mainId,
                      })
                    }
                  >
                    {canManageMedia && thumb ? (
                      <button
                        type="button"
                        className="absolute top-1 right-1 z-10 h-6 w-6 rounded-full bg-black/60 text-white text-xs hover:bg-black/75"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAppointmentRadiograph(mainId);
                        }}
                        title="Șterge radiografia"
                        aria-label="Șterge radiografia"
                      >
                        ✕
                      </button>
                    ) : null}
                    {thumb ? (
                      <img src={thumb} alt="Rx" className="object-cover h-full w-full" />
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Nicio radiografie salvată.</p>
          )}
        </div>
      </div>

      {/* Programări pacient */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h4 className="font-semibold">Programări</h4>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={appointmentsTab === "active" ? "primary" : "secondary"}
              onClick={() => setAppointmentsTab("active")}
            >
              Active
            </Button>
            <Button
              size="sm"
              variant={appointmentsTab === "history" ? "primary" : "secondary"}
              onClick={() => setAppointmentsTab("history")}
            >
              Istoric
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Ora</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Notițe</th>
              </tr>
            </thead>
            <tbody>
              {(appointmentsTab === "active"
                ? appointmentsByStatus.active
                : appointmentsByStatus.history
              ).length > 0 ? (
                (appointmentsTab === "active"
                  ? appointmentsByStatus.active
                  : appointmentsByStatus.history
                ).map((appointment) => {
                  const statusLabel = getStatusLabel(appointment.status);
                  const statusClass = getStatusClass(appointment.status);
                  const rescheduledTarget = appointment.rescheduledTo
                    ? appointmentsByStatus.byId.get(appointment.rescheduledTo)
                    : null;
                  return (
                    <tr key={appointment.id} className="border-t">
                      <td className="px-3 py-2">{formatAppointmentDate(appointment)}</td>
                      <td className="px-3 py-2">{formatAppointmentTime(appointment)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-semibold ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {appointment.notes || "-"}
                        {appointment.cancelNote ? (
                          <div className="text-xs text-red-600 mt-1">
                            Motiv anulare: {appointment.cancelNote}
                          </div>
                        ) : null}
                        {appointment.rescheduleNote ? (
                          <div className="text-xs text-yellow-700 mt-1">
                            Motiv reprogramare: {appointment.rescheduleNote}
                          </div>
                        ) : null}
                        {rescheduledTarget ? (
                          <div className="text-xs text-gray-500 mt-1">
                            Reprogramată către: {formatAppointmentDate(rescheduledTarget)}{" "}
                            {formatAppointmentTime(rescheduledTarget)}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                    Nicio programare.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Butoane */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:items-center sm:justify-between">
        {canEditPatient && (
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <Button variant="primary" onClick={handleSave}>💾 Salvează</Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFormData(normalizePatientData(patient));
                    setEditing(false);
                  }}
                >
                  Anulează
                </Button>
              </>
            ) : (
              <Button variant="warning" onClick={() => setEditing(true)}>✏️ Editează</Button>
            )}
          </div>
        )}

        {canUploadRadiography && (
          <div className="flex flex-wrap gap-2 justify-end">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAddRadiograph}
            />
            <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
              ➕ Adaugă radiografie
            </Button>
          </div>
        )}
      </div>

      {/* Modal preview Rx */}
      {previewRx && (
        <RadiographyPreviewModal
          previewImage={previewRx}
          onClose={() => setPreviewRx(null)}
          onDownload={handlePreviewDownload}
        />
      )}
      {showRepresentativeModal && (
        <EditRepresentativeModal
          initialData={formData.legalRepresentative || {}}
          onConfirm={handleRepresentativeModalConfirm}
          onCancel={handleRepresentativeModalCancel}
        />
      )}
    </Modal>
  );
}

function EditRepresentativeModal({ initialData, onConfirm, onCancel }) {
  const [draft, setDraft] = useState({
    lastName: initialData.lastName || "",
    firstName: initialData.firstName || "",
    city: initialData.city || "",
    relation: initialData.relation || "",
  });

  useEffect(() => {
    setDraft({
      lastName: initialData.lastName || "",
      firstName: initialData.firstName || "",
      city: initialData.city || "",
      relation: initialData.relation || "",
    });
  }, [initialData]);

  const handleChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const nameRegex = /^[A-Za-zĂÂÎȘȚăâîșț\-'\s]{2,}$/u;
    const lastName = draft.lastName.trim();
    const firstName = draft.firstName.trim();
    const city = draft.city.trim();
    const relation = draft.relation.trim();

    if (!nameRegex.test(lastName)) {
      toast.error("Introdu un nume valid pentru reprezentant.");
      return;
    }
    if (!nameRegex.test(firstName)) {
      toast.error("Introdu un prenume valid pentru reprezentant.");
      return;
    }
    if (!city) {
      toast.error("Completează orașul reprezentantului legal.");
      return;
    }
    if (!relation) {
      toast.error("Specifică calitatea reprezentantului legal.");
      return;
    }

    onConfirm({ lastName, firstName, city, relation });
  };

  const renderField = (field, label) => {
    const value = draft[field] || "";
    const placeholder = value ? "" : label;
    return (
      <label className="block text-sm text-gray-700">
        <span>{label}</span>
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => handleChange(field, e.target.value)}
          className="border rounded w-full px-2 py-2 text-sm mt-1"
        />
      </label>
    );
  };

  return (
    <Modal title="Reprezentant legal" onClose={onCancel} size="sm">
      <div className="p-4 space-y-3">
        {renderField("lastName", "Nume")}
        {renderField("firstName", "Prenume")}
        {renderField("city", "Oraș")}
        {renderField("relation", "Calitate")}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onCancel}>
            Renunță
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Continua
          </Button>
        </div>
      </div>
    </Modal>
  );
}
